import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { isoServices } from '../mockData';
import { getIcon } from '../utils/icons';
import { ArrowRight, CheckCircle } from 'lucide-react';

const CertificationPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#003f6d] to-[#004a85] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">ISO Certification Services</h1>
          <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
          <p className="text-xl text-gray-200 max-w-3xl">
            QSC America provides certification services for critical international standards that help organizations enhance quality, security, and operational excellence.
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Get ISO Certified?</h2>
              <div className="space-y-4">
                {[
                  'Demonstrate commitment to quality and excellence',
                  'Gain competitive advantage in your market',
                  'Improve operational efficiency and reduce costs',
                  'Meet regulatory and customer requirements',
                  'Enhance stakeholder trust and confidence',
                  'Access new markets and business opportunities'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#d4af37] flex-shrink-0 mt-1" />
                    <p className="text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjkwMzI4MzV8MA&ixlib=rb-4.1.0&q=85"
                alt="ISO Certification Benefits"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ISO Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our ISO Standards
            </h2>
            <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized certification services for the most critical international standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {isoServices.map((service) => {
              const Icon = getIcon(service.icon);
              return (
                <Card
                  key={service.id}
                  className="group hover:shadow-2xl transition-all duration-300 border-t-4"
                  style={{ borderTopColor: service.color }}
                >
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div
                        className="p-4 rounded-lg"
                        style={{ backgroundColor: `${service.color}15` }}
                      >
                        <Icon className="w-10 h-10" style={{ color: service.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-3xl font-bold text-gray-900 mb-2">{service.code}</h3>
                        <h4 className="text-xl font-semibold text-gray-700">
                          {service.title}
                        </h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.shortDescription}</p>
                    <div className="mb-6">
                      <h5 className="font-semibold text-gray-900 mb-3">Key Benefits:</h5>
                      <ul className="space-y-2">
                        {service.benefits.slice(0, 3).map((benefit, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link to={`/certification/${service.id}`}>
                      <Button
                        className="w-full bg-[#003f6d] hover:bg-[#002a4d] text-white"
                      >
                        Learn More & Get Certified
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#003f6d] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Not Sure Which Standard is Right for You?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Our certification experts are here to help you choose the best path forward
          </p>
          <Link to="/contact">
            <Button className="bg-[#d4af37] hover:bg-[#c49b2a] text-[#003f6d] font-semibold px-8 py-6 text-lg">
              Request a Certification Briefing
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CertificationPage;
